/**
 * computeMargin.ts — pure per-project margin analytics (SAAS-03).
 *
 * Computes billedTotal and baseCostTotal by calling computeEstimateTotals TWICE:
 *   Pass 1 — billed:    current item prices + safetyMargin, vatRate=0 (pre-VAT margin).
 *   Pass 2 — base cost: catalog base prices + safetyMargin=0, vatRate=0.
 *
 * INV-1: All arithmetic uses parseSafeNumber/roundTo (CLAUDE.md §8).
 *        billedTotal === 0 ⇒ marginPercent = 0 (no NaN, no division-by-zero).
 *        All returned values are finite.
 *
 * ASSUMPTION A2: Margin is computed on netTotal (pre-VAT). VAT is a pass-through
 *   for the contractor; the contractor's economic profit is pre-VAT.
 * ASSUMPTION A3: "Base cost" = catalog priceLabor with no safety margin.
 *   Does NOT reflect actual purchase/material cost (deferred to v2).
 *
 * FORBIDDEN: Math.round, toFixed, /100, unguarded division — use roundTo only.
 */

import { computeEstimateTotals } from '@/entities/estimate';
import { parseSafeNumber, roundTo } from '@/shared/lib/number';
import type { Room } from '@/entities/room';
import type { EstimateItem } from '@/entities/estimate';
import type { Service } from '@/entities/service';

// ---------------------------------------------------------------------------
// Result type
// ---------------------------------------------------------------------------

export interface MarginResult {
  /** Project total at user prices + safetyMargin, pre-VAT (vatRate=0 pass). */
  billedTotal: number;
  /** Project total at catalog base prices, no safetyMargin, pre-VAT. */
  baseCostTotal: number;
  /** roundTo(billedTotal - baseCostTotal, 2). May be negative if base > billed. */
  profit: number;
  /**
   * Margin percentage: roundTo((profit / billedTotal) * 100, 1).
   * 0 when billedTotal === 0 (guard: no NaN / no division-by-zero). INV-1.
   */
  marginPercent: number;
}

// ---------------------------------------------------------------------------
// Core function
// ---------------------------------------------------------------------------

/**
 * Computes margin metrics from project data.
 *
 * @param rooms           - Rooms with their items (geometry is recalculated internally).
 * @param projectItems    - Project-level (global) items outside any room.
 * @param safetyMargin    - Safety/waste margin in percent (e.g. 10 = 10%).
 * @param services        - Base catalog services (from useServiceStore.rawServices).
 *                          Used to look up catalog priceLabor for Pass 2.
 * @param projectOverrides - Optional manual overrides for project-level geometry.
 */
export function computeMargin(
  rooms: Room[],
  projectItems: EstimateItem[],
  safetyMargin: number,
  services: Service[],
  projectOverrides?: { floorArea?: number; wallArea?: number; perimeter?: number },
): MarginResult {
  // ------------------------------------------------------------------
  // Pass 1: billed totals — user item prices, safetyMargin, vatRate=0
  // ------------------------------------------------------------------
  const billedResult = computeEstimateTotals(
    rooms,
    projectItems,
    safetyMargin,
    0, // vatRate: margin is pre-VAT (Assumption A2)
    projectOverrides,
  );
  const billedTotal = billedResult.netTotal;

  // ------------------------------------------------------------------
  // Pass 2: base cost — catalog prices, safetyMargin=0, vatRate=0
  // ------------------------------------------------------------------
  // Build serviceId → base priceLabor map from the catalog
  const servicePriceMap = new Map<string, number>(
    services.map((s) => [s.id, parseSafeNumber(s.priceLabor, 0)]),
  );

  // Remap each projectItem's priceLabor to the catalog base price.
  // Falls back to the item's own priceLabor when serviceId is absent from catalog
  // (unknown/custom service — base cost = user price → zero markup contribution).
  const baseProjectItems: EstimateItem[] = projectItems.map((item) => ({
    ...item,
    priceLabor: servicePriceMap.has(item.serviceId)
      ? (servicePriceMap.get(item.serviceId) as number)
      : parseSafeNumber(item.priceLabor, 0),
  }));

  // Remap room items' priceLabor to catalog base prices
  const baseRooms: Room[] = rooms.map((room) => ({
    ...room,
    items: room.items.map((item) => ({
      ...item,
      priceLabor: servicePriceMap.has(item.serviceId)
        ? (servicePriceMap.get(item.serviceId) as number)
        : parseSafeNumber(item.priceLabor, 0),
    })),
  }));

  const baseResult = computeEstimateTotals(
    baseRooms,
    baseProjectItems,
    0, // safetyMargin=0: base cost excludes waste uplift (Assumption A3)
    0, // vatRate=0
    projectOverrides,
  );
  const baseCostTotal = baseResult.netTotal;

  // ------------------------------------------------------------------
  // Derived: profit and marginPercent
  // ------------------------------------------------------------------
  const profit = roundTo(billedTotal - baseCostTotal, 2);

  // Guard division-by-zero: billedTotal === 0 → marginPercent = 0 (INV-1)
  const marginPercent =
    billedTotal > 0
      ? roundTo(((billedTotal - baseCostTotal) / billedTotal) * 100, 1)
      : 0;

  return { billedTotal, baseCostTotal, profit, marginPercent };
}
